import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";

import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import FileIcon from "~/components/file-type-icon";
import DocumentDescription from "~/components/document-description";
import Categories from "~/components/Processes/conversations/utils/categories.json";
import DocumentConfirmationDialog from "~/components/confirmation-dialog/DocumentConfirmationDialog";
import TemporaryNotification from "~/components/toast-notification/AttachmentTemporaryNotification";
import AttachmentSuccess from "~/components/toast-notification/AttachmentSuccess";
import {
  downloadFileHandler,
  openPreviewInNewTab,
  resolveDisplayFileName,
} from "~/utils/fileUtils";
import { formatDate } from "~/utils/CSVFunctions";
import useMetadataProcessor from "~/hooks/useMetadataProcessor";

/**
 * Convert MM/YYYY date in a displayName to "MonthName YYYY" and strip
 * the trailing file-type tag like "(PNG)" since FileIcon appends its own.
 * e.g. "PAYROLL_OUTPUT_FILE – 02/2026 (PNG)" → "PAYROLL_OUTPUT_FILE – February 2026"
 */
const formatDisplayNameDate = (name) => {
  if (!name) return name;
  // Convert MM/YYYY → MonthName YYYY
  let result = name.replace(/(\d{2})\/(\d{4})/, (_, month, year) => {
    const date = new Date(Number(year), Number(month) - 1);
    return date.toLocaleString("default", { month: "long" }) + " " + year;
  });
  // Remove trailing file-type tag e.g. " (PNG)", " (PDF)" — FileIcon adds its own
  result = result.replace(/\s*\([A-Z]{2,5}\)\s*$/, "");
  // Convert SNAKE_CASE_CODE before the separator to Title Case
  // e.g. "SALARY_AND_COMPENSATION_AGREEMENTS – February 2026" → "Salary And Compensation Agreements – February 2026"
  result = result.replace(/^([A-Z][A-Z0-9_]+)/, (match) =>
    match
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
  );
  return result;
};

const RecentDocuments = ({ entityId }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { id: userId, role } = useSelector((state) => state.login);
  const { document_folders, entity_detail } = useSelector((state) => ({
    document_folders: state.attachments.document_folders,
    entity_detail: state.globalEntities.entity_detail,
  }));

  // Build the full folder list including virtual folders (Core Entity Information, Ad Hoc Requests)
  // to match what useFileDocuments.js constructs
  const allFolders = useMemo(() => {
    const folders = [...(document_folders || [])];
    const hasEntityPlusAccess = entity_detail?.active_services?.some(
      (e) => e.name === "Entities +",
    );
    if (hasEntityPlusAccess) {
      folders.unshift({
        id: "a9c07a03-326c-45e5-b644-a795f0cbe67r",
        name: "Core Entity Information",
      });
    }
    folders.push({
      id: "a9c07a03-326c-45e5-b644-a795f0cbe67f",
      name: "Ad Hoc Requests",
    });
    return folders;
  }, [document_folders, entity_detail]);
  const menuRef = useRef(null);

  const getSubCategoryName = (categoryCode, subCategoryCode) => {
    const cat = Categories?.find((c) => c.code === categoryCode);
    return (
      cat?.subCategory?.find((s) => s.code === subCategoryCode)?.name || "-"
    );
  };

  const [loading, setLoading] = useState(false);
  const [recentFiles, setRecentFiles] = useState([]);
  const { processedData, loading: metadataLoading } =
    useMetadataProcessor(recentFiles);
  const [rowOverrides, setRowOverrides] = useState({});
  const tableData = useMemo(() => {
    const data = processedData?.length ? processedData : recentFiles;
    return [...data]
      .map((row) => {
        const key = row?.blob_url || row?.blobName || row?.name;
        const overrides = key ? rowOverrides[key] : null;
        return overrides ? { ...row, ...overrides } : row;
      })
      .sort((a, b) => {
        const dateA = new Date(a?.updatedAt || a?.createdAt || 0);
        const dateB = new Date(b?.updatedAt || b?.createdAt || 0);
        return dateB - dateA;
      });
  }, [processedData, recentFiles, rowOverrides]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [restoreMessage, setRestoreMessage] = useState("");
  const [detailsRow, setDetailsRow] = useState(null);

  const fetchRecentDocuments = async (skipCache = true) => {
    try {
      setLoading(true);
      const { data } = await apiService(
        API_ROUTES.FETCH_RECENT_DOCUMENTS,
        "GET",
        {
          replace: { entityId: entityId || id },
          skipCache,
        },
      );
      setRecentFiles(data || []);
    } catch (error) {
      console.error("Error fetching recent documents:", error);
      setRecentFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityId || id) {
      fetchRecentDocuments();
    }
  }, [entityId, id]);

  const handlePreviewFile = (file) => {
    openPreviewInNewTab(
      { objectURL: file?.blob_url },
      file?.blob_url?.includes("/ess") ? "ess" : "document-repo",
    );
  };

  const handleDownloadFile = (file) => {
    downloadFileHandler(
      { blob_url: file?.blob_url, name: file?.name },
      file?.blob_url?.includes("/ess") ? "ess" : "document-repo",
    );
  };

  const handleDeleteDocument = (file) => {
    setSelectedFile(file);
    setAttachmentConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setTemporaryMessage("");
    setRestoreMessage("");
    try {
      await apiService(API_ROUTES.DELETE_ATTACHMENT, "DELETE", {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: {
          relatedId: selectedFile?.related_id || entityId || id,
          relatedType: selectedFile?.related_type || "ENTITIES_PLUS",
        },
      });
      setTimeout(async () => {
        await fetchRecentDocuments(true);
        setAttachmentConfirmModal(false);
        const deleteDisplayName = formatDisplayNameDate(
          selectedFile?.displayName ||
            resolveDisplayFileName(
              selectedFile,
              tableData,
              getSubCategoryName,
            ) ||
            selectedFile?.name,
        );
        setTemporaryMessage(`${deleteDisplayName} has been deleted.`);
        setIsAttachmentLoader(false);
      }, 2000);
    } catch (error) {
      console.error("Error deleting document:", error);
      setIsAttachmentLoader(false);
    }
  };

  const handleRestoreAttachment = async () => {
    setTemporaryMessage("");
    setRestoreMessage("");
    try {
      await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
        body: { blobUrl: selectedFile?.blob_url },
      });
      setTimeout(async () => {
        await fetchRecentDocuments(true);
        const restoreDisplayName = formatDisplayNameDate(
          selectedFile?.displayName ||
            resolveDisplayFileName(
              selectedFile,
              tableData,
              getSubCategoryName,
            ) ||
            selectedFile?.name,
        );
        setRestoreMessage(`${restoreDisplayName} has been restored.`);
      }, 2000);
    } catch (error) {
      console.error("Error restoring document:", error);
    }
  };

  const handleViewInFolder = (file) => {
    const eId = entityId || id;
    const metadata = file?.metadata || file;
    const categoryCode = file?.category || metadata?.category;
    const navState = categoryCode ? { state: { categoryCode } } : undefined;

    // Helper to find folder ID by name from allFolders (includes virtual folders)
    const getFolderIdByName = (name) =>
      allFolders?.find((f) => f.name === name)?.id;

    // Process or Payslip documents → services/:service_id/:year/process/:process_id
    const processData = file?.process_data?.data || file?.payslip_data?.data;
    if (processData?.serviceData?.id) {
      const serviceId = processData.serviceData.id;
      const year = processData.start_date
        ? new Date(processData.start_date).getFullYear()
        : null;
      const processId = file.process_uuid || file.payslip_uuid;
      if (year && processId) {
        navigate(
          `/files/entity/${eId}/services/${serviceId}/${year}/process/${processId}`,
          navState,
        );
      } else {
        navigate(`/files/entity/${eId}/services/${serviceId}`);
      }
      return;
    }

    // Core Entity Information documents (officer/director/shareholder/entity-level)
    const coreEntityServiceId = getFolderIdByName("Core Entity Information");

    // Officer documents
    if (file?.officer_data && coreEntityServiceId) {
      const typeCode =
        file.officer_data.data?.type === "officer"
          ? "officers"
          : "power-of-attorney";
      const officerId = file.officer_uuid;
      if (officerId) {
        navigate(
          `/files/entity/${eId}/services/${coreEntityServiceId}/${typeCode}/${officerId}`,
          navState,
        );
      } else {
        navigate(
          `/files/entity/${eId}/services/${coreEntityServiceId}/${typeCode}`,
        );
      }
      return;
    }

    // Director documents
    if (file?.director_data && coreEntityServiceId) {
      const directorId = file.director_uuid;
      if (directorId) {
        navigate(
          `/files/entity/${eId}/services/${coreEntityServiceId}/directors/${directorId}`,
          navState,
        );
      } else {
        navigate(
          `/files/entity/${eId}/services/${coreEntityServiceId}/directors`,
        );
      }
      return;
    }

    // Shareholder documents
    if (file?.shareHolder_data && coreEntityServiceId) {
      const shareholderId = file.shareHolder_uuid;
      if (shareholderId) {
        navigate(
          `/files/entity/${eId}/services/${coreEntityServiceId}/shareholder/${shareholderId}`,
          navState,
        );
      } else {
        navigate(
          `/files/entity/${eId}/services/${coreEntityServiceId}/shareholder`,
        );
      }
      return;
    }

    // Core Entity Information (entity-level) without officer/director/shareholder
    if (metadata?.core_entity_info && coreEntityServiceId) {
      navigate(
        `/files/entity/${eId}/services/${coreEntityServiceId}/entity-information`,
        navState,
      );
      return;
    }

    // Ad Hoc Request documents
    const adHocServiceId = getFolderIdByName("Ad Hoc Requests");
    if (file?.request_data && metadata?.request_reason && adHocServiceId) {
      const year = metadata.year || file.year;
      const requestType = metadata.request_reason.toLowerCase();
      const requestId = file.request_uuid;
      if (year && requestId) {
        navigate(
          `/files/entity/${eId}/requests/${adHocServiceId}/${year}/request-type/${requestType}/${requestId}`,
          navState,
        );
      } else if (year) {
        navigate(
          `/files/entity/${eId}/requests/${adHocServiceId}/${year}/request-type/${requestType}`,
        );
      } else {
        navigate(`/files/entity/${eId}/requests/${adHocServiceId}`);
      }
      return;
    }

    // Escalation documents → navigate to the parent process
    if (file?.escalation_data?.data) {
      const escProcessData = file.escalation_data.data;
      const serviceId = escProcessData?.serviceData?.id;
      const year = escProcessData?.start_date
        ? new Date(escProcessData.start_date).getFullYear()
        : null;
      const processId = file.escalated_process_uuid;
      if (serviceId && year && processId) {
        navigate(
          `/files/entity/${eId}/services/${serviceId}/${year}/process/${processId}`,
          navState,
        );
      } else if (serviceId) {
        navigate(`/files/entity/${eId}/services/${serviceId}`);
      }
      return;
    }

    // Fallback: navigate to entity root
    navigate(`/files/entity/${eId}`);
  };

  const canDeleteFile = (file) => {
    return (
      ["GXM_ADMIN", "GXM_USER"].includes(role) || file?.created_by === userId
    );
  };

  const getContextMenuItems = (file) => {
    const items = [
      {
        label: "Preview File",
        icon: "pi pi-external-link",
        command: () => handlePreviewFile(file),
      },
      {
        label: "View File Details",
        icon: "pi pi-info-circle",
        command: () => {
          setDetailsRow(getRowWithOverrides(file));
          setSelectedFile(null);
        },
      },
      {
        label: "Download",
        icon: "pi pi-download",
        command: () => handleDownloadFile(file),
      },
    ];
    if (canDeleteFile(file)) {
      items.push({
        label: "Delete File",
        icon: "pi pi-trash",
        command: () => handleDeleteDocument(file),
      });
    }
    items.push({
      label: "View in Folder",
      icon: "pi pi-folder-open",
      command: () => handleViewInFolder(file),
    });
    return items;
  };

  const nameBodyTemplate = (rowData) => {
    const row = getRowWithOverrides(rowData);
    const displayName = formatDisplayNameDate(
      row?.displayName ||
        resolveDisplayFileName(row, tableData, getSubCategoryName),
    );
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
          className="cursor-pointer"
          onClick={() => handlePreviewFile(rowData)}
          style={{ flexShrink: 0 }}
        >
          <FileIcon
            filetype={row?.file_type}
            downloadFile={() => handlePreviewFile(rowData)}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span
            className="file__icon__name cursor-pointer"
            onClick={() => handlePreviewFile(rowData)}
          >
            {displayName || decodeURIComponent(row?.name)}
          </span>
          {metadataLoading ? (
            <Skeleton width="60%" height="0.8rem" />
          ) : (
            row?.breadcrumb && (
              <span
                className="text-color-secondary"
                style={{ fontSize: "0.8rem", color: "#6c757d" }}
              >
                {row.breadcrumb}
              </span>
            )
          )}
        </div>
      </div>
    );
  };

  const descriptionBodyTemplate = (rowData) => {
    const row = getRowWithOverrides(rowData);
    const displayDesc = row?.description;
    return (
      <div className="flex align-items-center justify-content-between">
        <p style={{ margin: 0, wordBreak: "break-word", whiteSpace: "normal" }}>
          {displayDesc?.trim()
            ? decodeURIComponent(displayDesc)
            : "Add description"}
        </p>
        <span
          className="pi pi-angle-right cursor-pointer"
          onClick={() => setDetailsRow(getRowWithOverrides(rowData))}
          role="button"
          aria-label="View description details"
        />
      </div>
    );
  };

  const getRowKey = (row) => row?.blob_url || row?.blobName || row?.name;

  const getRowWithOverrides = (rowData) => {
    const key = getRowKey(rowData);
    const overrides = key ? rowOverrides[key] : null;
    return overrides ? { ...rowData, ...overrides } : rowData;
  };

  const subcategoryBodyTemplate = (rowData) => {
    const row = getRowWithOverrides(rowData);
    const subCategory = Categories.find(
      (c) => c.code === row?.category,
    )?.subCategory?.find?.((s) => s.code === row?.subCategory)?.name;
    return subCategory || "-";
  };

  const lastModifiedBodyTemplate = (rowData) => {
    const row = getRowWithOverrides(rowData);
    return formatDate(row?.updatedAt || row?.createdAt) || "-";
  };

  const actionsBodyTemplate = (rowData) => (
    <div className="flex align-items-center gap-2">
      <span
        className="pi pi-info-circle cursor-pointer pencil-pointer"
        onClick={() => setDetailsRow(getRowWithOverrides(rowData))}
        role="button"
        aria-label="View file details"
      />
      <span
        className="pi pi-download pencil-pointer"
        onClick={() => handleDownloadFile(rowData)}
        role="button"
        aria-label="Download"
      />
      <i
        className="pi pi-ellipsis-v cursor-pointer"
        onClick={(e) => {
          setSelectedFile(rowData);
          menuRef.current.toggle(e);
        }}
      />
    </div>
  );

  const emptyMessageTemplate = (
    <p
      style={{
        textAlign: "center",
        padding: "2rem 0",
        color: "#6c757d",
        margin: 0,
      }}
    >
      No recent files
    </p>
  );

  return (
    <div className="recent-documents-section">
      <h3 className="recent-documents-header">Recents</h3>
      <Menu
        model={getContextMenuItems(selectedFile)}
        popup
        ref={menuRef}
        popupAlignment="right"
      />
      <DocumentConfirmationDialog
        visible={attachmentConfirmModal}
        closeModal={() => setAttachmentConfirmModal(false)}
        handleSave={handleConfirmDelete}
        isLoader={isAttachmentLoader}
      />
      <AttachmentSuccess
        successMsg={restoreMessage}
        setMessages={setRestoreMessage}
      />
      <TemporaryNotification
        onUndo={handleRestoreAttachment}
        successMsg={temporaryMessage}
        setMessages={setTemporaryMessage}
      />
      {loading ? (
        <DataTable
          value={[...Array(5)]}
          tableStyle={{ minWidth: "50rem" }}
          className="datatable-right-align"
        >
          <Column
            header="Name"
            body={() => <Skeleton width="100%" />}
            style={{ width: "600px" }}
          />
          <Column header="Description" body={() => <Skeleton width="100%" />} />
          <Column header="Subcategory" body={() => <Skeleton width="100%" />} />
          <Column
            header="Last Modified"
            body={() => <Skeleton width="100%" />}
          />
          <Column body={() => <Skeleton width="2rem" />} />
        </DataTable>
      ) : (
        <DataTable
          value={tableData}
          tableStyle={{ minWidth: "50rem" }}
          paginator={false}
          emptyMessage={emptyMessageTemplate}
          className="datatable-right-align"
        >
          <Column
            header="Name"
            body={nameBodyTemplate}
            sortable
            field="name"
            style={{ width: "600px" }}
          />
          <Column
            header="Description"
            body={descriptionBodyTemplate}
            sortable
            field="description"
          />
          <Column header="Subcategory" body={subcategoryBodyTemplate} />
          <Column
            header="Last Modified"
            body={lastModifiedBodyTemplate}
            field="updatedAt"
          />
          <Column body={actionsBodyTemplate} style={{ width: "80px" }} />
        </DataTable>
      )}

      {detailsRow && (
        <DocumentDescription
          data={detailsRow}
          id={detailsRow?.related_id || entityId || id}
          type={detailsRow?.related_type || "ENTITIES_PLUS"}
          visible={!!detailsRow}
          onVisibleChange={(v) => !v && setDetailsRow(null)}
          hideTrigger
          onRefetch={fetchRecentDocuments}
          onDataUpdate={(updatedRow) => {
            const key = getRowKey(detailsRow);
            if (key) {
              const patch = { ...updatedRow };
              patch.displayName = resolveDisplayFileName(
                { ...detailsRow, ...patch },
                tableData,
                getSubCategoryName,
              );
              setRowOverrides((prev) => ({
                ...prev,
                [key]: { ...(prev[key] || {}), ...patch },
              }));
            }
            setDetailsRow((prev) => ({ ...prev, ...updatedRow }));
          }}
          onDescriptionUpdated={(updatedRow, newDesc) => {
            const key = getRowKey(updatedRow);
            if (key) {
              const now = new Date().toISOString();
              const patch =
                newDesc !== undefined
                  ? {
                      description: newDesc,
                      updatedAt: now,
                      lastModified: now,
                    }
                  : {
                      category: updatedRow.category,
                      subCategory: updatedRow.subCategory,
                      year: updatedRow.year,
                      month: updatedRow.month,
                      lastModified: updatedRow.lastModified || now,
                      updatedAt: updatedRow.updatedAt || now,
                    };

              // Recompute displayName when category/subcategory changes
              if (newDesc === undefined) {
                const mergedRow = { ...updatedRow, ...patch };
                patch.displayName = resolveDisplayFileName(
                  mergedRow,
                  tableData,
                  getSubCategoryName,
                );
              }

              setRowOverrides((prev) => ({
                ...prev,
                [key]: { ...(prev[key] || {}), ...patch },
              }));

              setDetailsRow((prev) =>
                prev && getRowKey(prev) === key ? { ...prev, ...patch } : prev,
              );
            }
          }}
        />
      )}
    </div>
  );
};

export default RecentDocuments;
